// Carrusel de venta de la portada: unas pocas cápsulas elegidas (las de
// "venta" en /capsulas/catalogo.json). El resto se ve solo dentro del panel.
//
// Un video grande y una tira de tarjetas: la destacada rota sola hasta que la
// persona elige una, y al pasar el mouse por una tarjeta se ve un adelanto
// mudo. Al darle play, el video suena y deja de rotar.
import React, { useEffect, useRef, useState } from 'react';
import { cargarCatalogo, duracionTexto, type Capsula } from '../utils/capsulas';
import { registrarVisita } from '../utils/visitas';

const C = { navy: '#0D1B35', navy2: '#13264A', blue2: '#2756A8', oro: '#E3B54F', claro: '#E8EDF5', suave: '#9DB0CF' };

const CarruselCapsulas: React.FC<{ onRegistro: () => void }> = ({ onRegistro }) => {
  const [caps, setCaps] = useState<Capsula[] | null>(null);
  const [i, setI] = useState(0);
  const [reproduciendo, setReproduciendo] = useState(false);
  const [elegida, setElegida] = useState(false);
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    cargarCatalogo().then((c) => {
      const v = c.venta.map((n) => c.capsulas.find((x) => x.num === n)).filter(Boolean) as Capsula[];
      setCaps(v.length ? v : null);
    }).catch(() => setCaps(null));
  }, []);

  // Rota sola mientras nadie haya elegido ni esté mirando un video.
  useEffect(() => {
    if (!caps || elegida || reproduciendo) return;
    const t = setInterval(() => setI((k) => (k + 1) % caps.length), 6500);
    return () => clearInterval(t);
  }, [caps, elegida, reproduciendo]);

  if (!caps) return null;
  const actual = caps[i];
  const elegir = (k: number) => { setElegida(true); setReproduciendo(false); setI(k); };
  const reproducir = () => {
    setElegida(true); setReproduciendo(true);
    registrarVisita('portada', 'video_carrusel');
    requestAnimationFrame(() => video.current?.play().catch(() => {}));
  };

  return (
    <section id="videos" aria-label="Videos de Tabularis" style={{ background: `linear-gradient(160deg, ${C.navy} 0%, ${C.navy2} 100%)`, color: C.claro, padding: 'clamp(40px, 6vw, 80px) 6vw', margin: '2vw 0' }}>
      <div style={{ maxWidth: 1160, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 20, flexWrap: 'wrap', marginBottom: 28 }}>
          <div>
            <div style={{ fontSize: 13, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.oro, fontWeight: 600 }}>Míralo funcionando</div>
            <h2 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 'clamp(26px, 3.4vw, 40px)', margin: '8px 0 0', lineHeight: 1.15, color: '#fff' }}>Un minuto por tarea. Así trabaja tu estudio en Tabularis.</h2>
          </div>
          <button onClick={onRegistro} style={{ background: C.oro, color: C.navy, border: 'none', borderRadius: 10, padding: '13px 22px', fontSize: 15, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Probar 14 días gratis →</button>
        </div>

        <div className="lp-carrusel" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.9fr) minmax(0, 1fr)', gap: 22, alignItems: 'start' }}>
          {/* El video destacado */}
          <div style={{ minWidth: 0 }}>
            <div style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.08)', aspectRatio: '16 / 9', background: '#000' }}>
              {reproduciendo ? (
                <video ref={video} key={actual.video} src={actual.video} poster={actual.poster} controls autoPlay playsInline
                  controlsList="nodownload" disablePictureInPicture onContextMenu={(e) => e.preventDefault()}
                  onEnded={() => setReproduciendo(false)} style={{ width: '100%', height: '100%', display: 'block' }} />
              ) : (
                <button onClick={reproducir} aria-label={`Ver: ${actual.titulo}`} style={{ all: 'unset', cursor: 'pointer', position: 'absolute', inset: 0 }}>
                  <img key={actual.poster} src={actual.poster} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', animation: 'lpFundido .6s ease' }} />
                  <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(13,27,53,0) 45%, rgba(13,27,53,0.85) 100%)' }} />
                  <span className="lp-play" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 'clamp(64px, 8vw, 92px)', height: 'clamp(64px, 8vw, 92px)', borderRadius: '50%', background: C.oro, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 10px rgba(227,181,79,0.25)' }}>
                    <svg viewBox="0 0 24 24" width="38%" height="38%" aria-hidden><path d="M7 4.5v15l12-7.5z" fill={C.navy} /></svg>
                  </span>
                  <span style={{ position: 'absolute', left: 20, right: 20, bottom: 16 }}>
                    <span className="lp-carrusel-titulo" style={{ display: 'block', fontSize: 'clamp(18px, 2vw, 24px)', fontWeight: 700, color: '#fff' }}>{actual.titulo}</span>
                    <span className="lp-carrusel-resumen" style={{ display: 'block', fontSize: 14, color: C.suave, marginTop: 2 }}>{actual.resumen} · {duracionTexto(actual.duracion)}</span>
                  </span>
                </button>
              )}
            </div>
            {/* Puntos de avance de la rotación */}
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 14 }}>
              {caps.map((c, k) => (
                <button key={c.num} onClick={() => elegir(k)} aria-label={c.titulo}
                  style={{ width: k === i ? 26 : 8, height: 8, borderRadius: 4, border: 'none', cursor: 'pointer', background: k === i ? C.oro : 'rgba(255,255,255,0.25)', transition: 'width .3s' }} />
              ))}
            </div>
          </div>

          {/* La lista */}
          <div className="lp-carrusel-lista" style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
            {caps.map((c, k) => <Tarjeta key={c.num} c={c} activa={k === i} onClick={() => elegir(k)} />)}
          </div>
        </div>
      </div>
    </section>
  );
};

/** Tarjeta de la lista: al pasar el mouse muestra un adelanto mudo. */
const Tarjeta: React.FC<{ c: Capsula; activa: boolean; onClick: () => void }> = ({ c, activa, onClick }) => {
  const v = useRef<HTMLVideoElement>(null);
  const [encima, setEncima] = useState(false);
  useEffect(() => {
    if (!v.current) return;
    if (encima) { v.current.currentTime = Math.min(10, c.duracion / 4); v.current.play().catch(() => {}); } else v.current.pause();
  }, [encima, c.duracion]);
  return (
    <button onClick={onClick} onMouseEnter={() => setEncima(true)} onMouseLeave={() => setEncima(false)}
      style={{ display: 'flex', gap: 12, alignItems: 'center', textAlign: 'left', background: activa ? 'rgba(227,181,79,0.12)' : 'rgba(255,255,255,0.04)', border: `1px solid ${activa ? 'rgba(227,181,79,0.55)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 12, padding: 8, cursor: 'pointer', color: 'inherit', transition: 'background .2s, border-color .2s', minWidth: 0 }}>
      <span style={{ position: 'relative', width: 120, flexShrink: 0, aspectRatio: '16 / 9', borderRadius: 8, overflow: 'hidden', background: '#000' }}>
        <img src={c.poster} alt="" loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        {encima && <video ref={v} src={c.video} muted playsInline preload="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
        <span style={{ position: 'absolute', right: 4, bottom: 4, background: 'rgba(0,0,0,0.75)', color: '#fff', fontSize: 10.5, borderRadius: 4, padding: '0 5px' }}>{duracionTexto(c.duracion)}</span>
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: '#fff', lineHeight: 1.25 }}>{c.titulo}</span>
        <span style={{ fontSize: 12.5, color: C.suave, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' } as React.CSSProperties}>{c.resumen}</span>
      </span>
    </button>
  );
};

export default CarruselCapsulas;
